import React, { useState, useEffect } from 'react';
import { ArrowUpCircle, X, Sparkles, Droplets } from 'lucide-react';
import { calculateUpgradeCost } from '../utils/upgradeCalculator';
import { NATURES } from '../utils/gameData';

export default function LevelUpModal({ isOpen, onClose, mon, pokedex }) {
    const [targetLevel, setTargetLevel] = useState(2);
    const [includeEvolution, setIncludeEvolution] = useState(false);
    const [evoCandies, setEvoCandies] = useState(40);
    const [evoShards, setEvoShards] = useState(0);

    // Reset constraints whenever opened with a new Pokemon
    useEffect(() => {
        if (mon && pokedex) {
            setTargetLevel(Math.min(mon.level + 1, 65));
            setIncludeEvolution(false);
            
            const species = pokedex[mon.species_id];
            let defaultCandies = 40; // Default for Base -> Stage 1
            
            if (species) {
                // Hardcoded exceptions for Pokémon Sleep
                const EVOS_20 = ['pichu', 'cleffa', 'igglybuff', 'togepi', 'wynaut', 'bonsly', 'mime_jr', 'happiny'];
                const EVOS_80 = ['pikachu', 'clefairy', 'jigglypuff', 'growlithe', 'slowpoke', 'magneton', 'eevee', 'togetic', 'haunter', 'kirlia', 'riolu'];
                const EVOS_100 = ['dragonair', 'pupitar'];

                if (EVOS_20.includes(species.id)) defaultCandies = 20;
                else if (EVOS_80.includes(species.id)) defaultCandies = 80;
                else if (EVOS_100.includes(species.id)) defaultCandies = 100;
                else if (species.evolves_to.length === 0) defaultCandies = 0; // Fully Evolved
                else if (species.id !== species.family && species.evolves_to.length > 0) {
                    defaultCandies = 80; // Standard Middle Evolution (e.g., Ivysaur, Charmeleon)
                }
            }

            setEvoCandies(defaultCandies);
            setEvoShards(0);
        }
    }, [mon, isOpen, pokedex]);

    if (!isOpen || !mon || !pokedex) return null;

    const species = pokedex[mon.species_id];
    if (!species) return null;

    // Pseudo-legendaries (e.g. Dratini, Larvitar) have a slower leveling curve (1.5x)
    const isSlowCurve = ['Dratini', 'Larvitar'].includes(species.family);
    const nature = mon.nature || 'Serious';

    // Calculate base evolution inputs
    const activeEvoCandies = includeEvolution ? (parseInt(evoCandies) || 0) : 0;
    const activeEvoShards = includeEvolution ? (parseInt(evoShards) || 0) : 0;

    // Run the Maths Engine
    const { expNeeded, candiesNeeded, shardsNeeded } = calculateUpgradeCost(
        mon.level, 
        targetLevel, 
        nature, 
        isSlowCurve,
        activeEvoCandies,
        activeEvoShards
    );

    const hasExpUp = NATURES[nature]?.up === 'Exp';
    const hasExpDown = NATURES[nature]?.down === 'Exp';

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col ring-1 ring-gray-900/5 animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="bg-gray-800 p-4 flex justify-between items-center text-white shrink-0 shadow-md">
                    <div className="flex items-center gap-2">
                        <ArrowUpCircle size={20} className="text-green-400" />
                        <h2 className="text-lg font-black tracking-tight">Level Up Simulator</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm">
                        <X size={20} />
                    </button>
                </div>

                <div className="p-6 flex flex-col gap-5 bg-gray-50">
                    
                    {/* Selected Mon Header */}
                    <div className="flex items-center gap-4 bg-white p-3 rounded-xl border border-gray-200 shadow-sm">
                        <img src={`/images/pokemon/${species.id}.png`} className="w-12 h-12 object-contain drop-shadow" alt={species.name} />
                        <div>
                            <h3 className="font-black text-gray-800 text-lg leading-tight">{mon.nickname || species.name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                                <span className="bg-indigo-100 text-indigo-800 text-[10px] font-black px-2 py-0.5 rounded border border-indigo-200">Lv {mon.level}</span>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${hasExpUp ? 'bg-green-100 text-green-700 border-green-200' : hasExpDown ? 'bg-red-100 text-red-700 border-red-200' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                    {nature} {hasExpUp ? '(Exp ▲)' : hasExpDown ? '(Exp ▼)' : ''}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Settings Panel */}
                    <div className="space-y-4">
                        <div>
                            <label className="flex justify-between text-sm font-bold text-gray-700 mb-1">
                                <span>Target Level</span>
                                <span className="text-indigo-600">Lv {targetLevel}</span>
                            </label>
                            <input type="range" min={mon.level} max="65" value={targetLevel} onChange={(e) => setTargetLevel(parseInt(e.target.value))} className="w-full accent-indigo-600" />
                        </div>

                        <div className="bg-white border border-gray-200 rounded-xl p-3 shadow-sm">
                            <label className="flex items-center gap-2 cursor-pointer font-bold text-sm text-gray-700">
                                <input type="checkbox" className="w-4 h-4 text-indigo-600 accent-indigo-600 rounded border-gray-300 focus:ring-indigo-500" checked={includeEvolution} onChange={(e) => setIncludeEvolution(e.target.checked)} />
                                Simulate Evolution Costs
                            </label>
                            
                            {includeEvolution && (
                                <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100 animate-in fade-in slide-in-from-top-1">
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Evo Candies</label>
                                        <input type="number" min="0" value={evoCandies} onChange={(e) => setEvoCandies(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none" />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 uppercase tracking-wider mb-1">Evo Shards</label>
                                        <input type="number" min="0" step="10" value={evoShards} onChange={(e) => setEvoShards(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg text-sm font-bold focus:ring-2 focus:ring-indigo-500 outline-none" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Output Results Block */}
                    <div className="bg-indigo-600 rounded-xl p-4 text-white shadow-inner flex flex-col gap-3">
                        <h4 className="text-xs font-black uppercase tracking-widest text-indigo-200 text-center">Total Resources Needed</h4>
                        <div className="grid grid-cols-3 divide-x divide-indigo-500/50">
                            <div className="flex flex-col items-center justify-center">
                                <Sparkles size={16} className="text-yellow-300 mb-1" />
                                <span className="font-black text-lg leading-none">{expNeeded.toLocaleString()}</span>
                                <span className="text-[10px] font-bold text-indigo-200 mt-1">EXP</span>
                            </div>
                            <div className="flex flex-col items-center justify-center">
                                <span className="text-lg mb-0.5 leading-none">🍬</span>
                                <span className="font-black text-lg leading-none">{candiesNeeded.toLocaleString()}</span>
                                <span className="text-[10px] font-bold text-indigo-200 mt-1">Candies</span>
                            </div>
                            <div className="flex flex-col items-center justify-center">
                                <Droplets size={16} className="text-blue-300 mb-1" />
                                <span className="font-black text-lg leading-none">{shardsNeeded.toLocaleString()}</span>
                                <span className="text-[10px] font-bold text-indigo-200 mt-1">Shards</span>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}